import {
	requestApiGet,
	requestApiGetFile,
	requestApiPost,
} from "../api/BacklogApiEndpoint";
import type { BacklogConfig } from "../contracts/BacklogConfig";
import type { BacklogApiContext } from "../contracts/api/BacklogApiContext";
import type { BacklogResponse } from "../contracts/api/BacklogResponse";
import type { BacklogResponseFile } from "../contracts/api/BacklogResponseFile";
import type { Issue } from "../entities/Issue";
import type { RecentlyViewedIssue } from "../entities/RecentlyViewedIssue";
import type { RecentlyViewedProject } from "../entities/RecentlyViewedProject";
import type { RecentlyViewedWiki } from "../entities/RecentlyViewedWiki";
import type { Star } from "../entities/Star";
import type { StarCount } from "../entities/StarCount";
import type { User } from "../entities/User";
import type { UserActivity } from "../entities/UserActivity";
import { BacklogError } from "../errors/BacklogError";
import type { EmptyObject } from "../types/EmptyObject";
import { assert } from "../utils/BacklogUtil";
import type { AddRecentlyViewedIssueParams } from "./params/AddRecentlyViewedIssueParams";
import type { AddRecentlyViewedWikiParams } from "./params/AddRecentlyViewedWikiParams";
import type { CountUserReceivedStarsParams } from "./params/CountUserReceivedStarsParams";
import type { GetListOfRecentlyViewedIssuesParams } from "./params/GetListOfRecentlyViewedIssuesParams";
import type { GetListOfRecentlyViewedProjectsParams } from "./params/GetListOfRecentlyViewedProjectsParams";
import type { GetListOfRecentlyViewedWikisParams } from "./params/GetListOfRecentlyViewedWikisParams";
import type { GetReceivedStarListParams } from "./params/GetReceivedStarListParams";
import type { GetUserIconParams } from "./params/GetUserIconParams";
import type { GetUserParams } from "./params/GetUserParams";
import type { GetUserRecentUpdatesParams } from "./params/GetUserRecentUpdatesParams";

/**
 * Get User List ユーザー一覧の取得
 *
 * Returns list of users in your space.
 */
export function getUserList(
	config: BacklogConfig,
	context?: BacklogApiContext,
): Promise<BacklogResponse<User[]>> {
	return requestApiGet({
		config,
		path: "users",
		params: undefined,
		context,
	});
}

/**
 * Get User ユーザー情報の取得
 *
 * Returns information about user.
 */
export function getUser(
	config: BacklogConfig,
	params: GetUserParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<User>> {
	assert(params);
	return requestApiGet({
		config,
		path: "users/:userId",
		params,
		context: context,
	});
}

/**
 * Add User ユーザーの追加
 *
 * Adds new user to the space. “Project Administrator” cannot add “Admin” user. You can’t use this API at new plan space.
 *
 */
export function addUser(
	config: BacklogConfig,
	params: EmptyObject,
	context?: BacklogApiContext,
): Promise<BacklogResponse<EmptyObject>> {
	throw new BacklogError("not supported.");
}

/**
 * Update User ユーザーの更新
 *
 * Updates information about user. You can’t use this API at new plan space.
 *
 */
export function updateUser(
	config: BacklogConfig,
	params: EmptyObject,
	context?: BacklogApiContext,
): Promise<BacklogResponse<EmptyObject>> {
	throw new BacklogError("not supported.");
}

/**
 * Delete User ユーザーの削除
 *
 * Deletes user from the space. You can’t use this API at new plan space.
 *
 */
export function deleteUser(
	config: BacklogConfig,
	params: EmptyObject,
	context?: BacklogApiContext,
): Promise<BacklogResponse<EmptyObject>> {
	throw new BacklogError("not supported.");
}

/**
 * Get Own User 認証ユーザー情報の取得
 *
 * Returns own information about user.
 */
export function getOwnUser(
	config: BacklogConfig,
	context?: BacklogApiContext,
): Promise<BacklogResponse<User>> {
	return requestApiGet({
		config,
		path: "users/myself",
		params: undefined,
		context: context,
	});
}

/**
 * Get User Icon ユーザーアイコンの取得
 *
 * Downloads user icon.
 */
export function getUserIcon(
	config: BacklogConfig,
	params: GetUserIconParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<BacklogResponseFile>> {
	assert(params);
	return requestApiGetFile({
		config,
		path: "users/:userId/icon",
		params,
		context,
	});
}

/**
 * Get User Recent Updates ユーザーの最近の活動の取得
 *
 * Returns user’s recent updates
 */
export function getUserRecentUpdates(
	config: BacklogConfig,
	params: GetUserRecentUpdatesParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<UserActivity[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "users/:userId/activities",
		params,
		context: context,
	});
}

/**
 * Get Received Star List ユーザーの受け取ったスター一覧の取得
 *
 * Returns the list of stars that user received.
 */
export function getReceivedStarList(
	config: BacklogConfig,
	params: GetReceivedStarListParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Star[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "users/:userId/stars",
		params,
		context: context,
	});
}

/**
 * Count User Received Stars ユーザーの受け取ったスターの数の取得
 *
 * Returns the list of stars that user received.
 */
export function countUserReceivedStars(
	config: BacklogConfig,
	params: CountUserReceivedStarsParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<StarCount>> {
	assert(params);
	return requestApiGet({
		config,
		path: "users/:userId/stars/count",
		params,
		context: context,
	});
}

/**
 * Get List of Recently Viewed Issues 自分が最近見た課題一覧の取得
 *
 * Returns list of issues which the user viewed recently.
 */
export function getListOfRecentlyViewedIssues(
	config: BacklogConfig,
	params?: GetListOfRecentlyViewedIssuesParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<RecentlyViewedIssue[]>> {
	return requestApiGet({
		config,
		path: "users/myself/recentlyViewedIssues",
		context: context,
		params,
	});
}

/**
 * Add Recently Viewed Issue 自分が最近見た課題の追加
 *
 * Add an issue which the user viewed recently.
 */
export function addRecentlyViewedIssue(
	config: BacklogConfig,
	params: AddRecentlyViewedIssueParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Issue>> {
	assert(params);
	return requestApiPost({
		config,
		path: "users/myself/recentlyViewedIssues",
		context: context,
		params,
	});
}

/**
 * Get List of Recently Viewed Projects 自分が最近見たプロジェクト一覧の取得
 *
 * Returns list of projects which the user viewed recently.
 */
export function getListOfRecentlyViewedProjects(
	config: BacklogConfig,
	params?: GetListOfRecentlyViewedProjectsParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<RecentlyViewedProject[]>> {
	return requestApiGet({
		config,
		path: "users/myself/recentlyViewedProjects",
		context: context,
		params,
	});
}

/**
 * Get List of Recently Viewed Wikis 自分が最近見たWiki一覧の取得
 *
 * Returns list of Wikis which the user viewed recently.
 */
export function getListOfRecentlyViewedWikis(
	config: BacklogConfig,
	params?: GetListOfRecentlyViewedWikisParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<RecentlyViewedWiki[]>> {
	return requestApiGet({
		config,
		path: "users/myself/recentlyViewedWikis",
		context: context,
		params,
	});
}

/**
 * Add Recently Viewed Wiki 自分が最近見たWikiの追加
 *
 * AAdd a wiki which the user viewed recently.
 */
export function addRecentlyViewedWiki(
	config: BacklogConfig,
	params: AddRecentlyViewedWikiParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<RecentlyViewedWiki>> {
	assert(params);
	return requestApiPost({
		config,
		path: "users/myself/recentlyViewedWikis",
		context: context,
		params,
	});
}
